import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// GH-100: same resolution shape as IntakeQRCode — explicit env override wins,
// otherwise connect.* host implies prod intake, anything else falls back to QA.
function resolveIntakeBaseUrl() {
  if (process.env.REACT_APP_INTAKE_BASE_URL) return process.env.REACT_APP_INTAKE_BASE_URL;
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    if (window.location.hostname === 'connect.newtonhealthcore.com') return 'https://intake.newtonhealthcore.com';
  }
  return 'https://intake-qa.newtonhealthcore.com';
}
const INTAKE_BASE_URL = resolveIntakeBaseUrl();

const STATUS_COLORS = {
  NOT_STARTED: { bg: '#f3f4f6', color: '#6b7280' },
  OPENED:      { bg: '#dbeafe', color: '#1e40af' },
  IN_PROGRESS: { bg: '#fef3c7', color: '#92400e' },
  COMPLETED:   { bg: '#d1fae5', color: '#065f46' },
  EXPIRED:     { bg: '#fee2e2', color: '#991b1b' },
};

const NOTIF_STATUS_COLORS = {
  PENDING: { bg: '#fef3c7', color: '#92400e' },
  SENT:    { bg: '#d1fae5', color: '#065f46' },
  FAILED:  { bg: '#fee2e2', color: '#991b1b' },
};

export default function AdhocRequestList({ practiceId, refreshKey }) {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filter) params.form_status = filter;
      const [reqRes, statsRes] = await Promise.all([
        api.getIntakeAdhocRequests(practiceId, params),
        api.getIntakeAdhocStats(practiceId)
      ]);
      setRequests(reqRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [practiceId, filter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const remind = async (id) => {
    try {
      await api.sendIntakeAdhocReminder(practiceId, id);
      alert('Reminder sent');
      load();
    } catch (err) { alert(err.message); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this ad-hoc request? The form link will be deactivated.')) return;
    try {
      await api.cancelIntakeAdhoc(practiceId, id);
      load();
    } catch (err) { alert(err.message); }
  };

  // GH-100: build the same /sign-in URL the SMS/email notification ships.
  // The sign-in loginValidate endpoint looks up the form by
  // IntakeFormData.access_token (UUID_B), NOT IntakeAdhocRequest.access_token
  // (UUID_A) — so the URL must carry the form-level token (form_access_token).
  // The SMS/email path overrides this in IntakeAdhocService._sendNotification;
  // Copy URL builds the URL fresh and gets form_access_token directly from
  // the backend response. If a form record doesn't exist yet for the request
  // (rare — generation usually fires synchronously on create), the button
  // is disabled rather than copying a broken URL.
  const buildFormUrl = (req) => {
    const practiceRef = req.practice_guid || req.practice_id;
    const patientRef = req.patient_guid || req.patient_id;
    const apptRef = req.appointment_id || '';
    const token = req.form_access_token;
    return `${INTAKE_BASE_URL}/sign-in?PracticeID=${practiceRef}&PatientID=${patientRef}&AppointmentId=${apptRef}&adhoc_token=${token}`;
  };

  const copyUrl = async (req) => {
    if (!req.form_access_token) {
      alert('This form is still being generated — try again in a few seconds.');
      return;
    }
    try {
      await navigator.clipboard.writeText(buildFormUrl(req));
      setCopiedId(req.id);
      setTimeout(() => setCopiedId(prev => (prev === req.id ? null : prev)), 2000);
    } catch (err) {
      alert('Copy failed. URL: ' + buildFormUrl(req));
    }
  };

  return (
    <div>
      {/* Stats Row */}
      {stats && (
        <div style={s.statsRow}>
          {[
            { label: 'Total', value: stats.total, color: '#6b7280' },
            { label: 'Pending', value: stats.NOT_STARTED, color: '#6b7280' },
            { label: 'Sent', value: (stats.OPENED || 0) + (stats.IN_PROGRESS || 0), color: '#1e40af' },
            { label: 'Completed', value: stats.COMPLETED, color: '#065f46' },
            { label: 'Expired', value: stats.EXPIRED, color: '#991b1b' },
          ].map(st => (
            <div key={st.label} style={s.statCard}>
              <div style={{ fontSize: 20, fontWeight: 700, color: st.color }}>{st.value || 0}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{st.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div style={s.filterRow}>
        <select style={s.filterSelect} value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="NOT_STARTED">Not Started</option>
          <option value="OPENED">Opened</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="EXPIRED">Expired</option>
        </select>
        <button style={s.btnSmall} onClick={load} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Table */}
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Patient</th>
            <th style={s.th}>Reason</th>
            <th style={s.th}>Channel</th>
            <th style={s.th}>Notif.</th>
            <th style={s.th}>Form Status</th>
            <th style={s.th}>Sent At</th>
            <th style={s.th}>Expires</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests.map(req => {
            const sc = STATUS_COLORS[req.form_status] || STATUS_COLORS.NOT_STARTED;
            const nc = NOTIF_STATUS_COLORS[req.notification_status] || NOTIF_STATUS_COLORS.PENDING;
            const isActive = req.form_status !== 'COMPLETED' && req.form_status !== 'EXPIRED';
            return (
              <tr key={req.id}>
                <td style={s.td}>{req.patient_name || `Patient #${req.patient_id}`}</td>
                <td style={s.td}>{req.reason || '—'}</td>
                <td style={s.td}><span style={s.channelBadge}>{req.notification_channel}</span></td>
                <td style={s.td}>
                  <span style={{ ...s.badge, background: nc.bg, color: nc.color }}>
                    {req.notification_status}
                  </span>
                </td>
                <td style={s.td}>
                  <span style={{ ...s.badge, background: sc.bg, color: sc.color }}>
                    {req.form_status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={s.td}>{req.notification_sent_at ? new Date(req.notification_sent_at).toLocaleString() : '—'}</td>
                <td style={s.td}>{req.expires_at ? new Date(req.expires_at).toLocaleDateString() : '—'}</td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {isActive && (
                      <>
                        <button
                          style={{ ...s.actionBtn, opacity: req.form_access_token ? 1 : 0.5 }}
                          onClick={() => copyUrl(req)}
                          disabled={!req.form_access_token}
                          title={req.form_access_token ? 'Copy the patient-facing form URL to clipboard' : 'Form is still generating — refresh in a few seconds'}
                        >
                          {copiedId === req.id ? 'Copied!' : 'Copy URL'}
                        </button>
                        <button style={s.actionBtn} onClick={() => remind(req.id)} title="Send Reminder">
                          Remind
                        </button>
                        <button style={{ ...s.actionBtn, color: '#dc2626' }} onClick={() => cancel(req.id)} title="Cancel">
                          Cancel
                        </button>
                      </>
                    )}
                    {req.form_status === 'COMPLETED' && (
                      <span style={{ fontSize: 11, color: '#065f46' }}>Done</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {requests.length === 0 && !loading && (
        <p style={s.empty}>No ad-hoc requests yet. Click "Send Ad-Hoc Forms" above to get started.</p>
      )}
    </div>
  );
}

const s = {
  statsRow: { display: 'flex', gap: 12, marginBottom: 16 },
  statCard: { flex: 1, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', textAlign: 'center' },
  filterRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 },
  filterSelect: { padding: '5px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, outline: 'none' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  badge: { display: 'inline-block', fontSize: 11, padding: '2px 6px', borderRadius: 4, fontWeight: 500 },
  channelBadge: { display: 'inline-block', fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#f3f4f6', color: '#6b7280', fontWeight: 500 },
  actionBtn: { padding: '2px 8px', fontSize: 11, background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', color: '#4f46e5' },
  btnSmall: { padding: '5px 10px', fontSize: 12, background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', color: '#4f46e5' },
  empty: { textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 20 },
};
